/**
 * セキュリティ `/security`。
 *
 * タブは 3 つ:
 *   blocks  … 遮断 IP（エージェント自動 BAN + 運営者の手動遮断）
 *   sending … 送信レート違反（乗っ取り検知の主戦場）
 *   abuse   … 迷惑行為レポート
 *
 * 05-ui にある spam / clamav タブは、どちらもノード側メトリクスの可視化であり
 * ノード詳細の metrics タブと内容が重なるため、ここには置かない。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Gauge,
  MoreHorizontal,
  Search,
  ShieldAlert,
  ShieldOff,
  Undo2,
  UserCheck,
} from 'lucide-react';
import {
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  StatCard,
  StatusBadge,
  Textarea,
  cn,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { IP_RULE_SOURCE, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { endImpersonationById, fetchImpersonations } from '../lib/auth.js';
import { qk } from '../lib/query-keys.js';
import { ratioOf } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { SUSPEND_MODE_OPTIONS, type SuspendMode } from '../lib/suspension.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  AbuseReport,
  BlocklistEntry,
  BlocklistStats,
  ImpersonationSession,
  SendingViolation,
} from '../types/api.js';

const routeApi = getRouteApi(RID.security);

export function SecurityPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader title="セキュリティ" description="遮断 IP・送信レート違反・迷惑行為の検知状況。" />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'blocks', label: '遮断 IP', icon: Ban },
          { value: 'sending', label: '送信レート', icon: Gauge },
          { value: 'abuse', label: '迷惑行為', icon: ShieldAlert },
          { value: 'impersonations', label: '代理ログイン', icon: UserCheck },
        ]}
      />

      {search.tab === 'blocks' ? <BlocksTab /> : null}
      {search.tab === 'sending' ? <SendingTab /> : null}
      {search.tab === 'abuse' ? <AbuseTab /> : null}
      {search.tab === 'impersonations' ? <ImpersonationsTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------------ 遮断 IP */

function BlocksTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const [blockOpen, setBlockOpen] = React.useState(false);

  const stats = useQuery({
    queryKey: qk.blocklistStats(),
    queryFn: () => api.get<BlocklistStats>('/blocklist/stats'),
    refetchInterval: 30_000,
  });

  const list = useCursorList<BlocklistEntry>({
    path: '/blocklist',
    keyFor: (params) => qk.blocklist(params),
    filters: { q: search.q, source: search.source, nodeId: search.nodeId },
    refetchInterval: 30_000,
  });

  const columns: Array<DataTableColumn<BlocklistEntry>> = [
    { id: 'cidr', header: 'IP / CIDR', cell: (b) => <code className="font-mono text-sm">{b.cidr}</code> },
    {
      id: 'source',
      header: '理由',
      cell: (b) => (
        <div className="space-y-0.5">
          <Badge variant="muted" size="sm">
            {labelOf(b.source)}
          </Badge>
          {b.reason ? <span className="block text-xs text-muted-foreground">{b.reason}</span> : null}
        </div>
      ),
    },
    {
      id: 'node',
      header: '検知ノード',
      hideBelow: 'md',
      cell: (b) =>
        b.nodeId ? (
          <Link
            to="/nodes/$nodeId"
            params={{ nodeId: b.nodeId }}
            search={{ tab: 'overview', range: '24h' }}
            className="hover:text-primary"
          >
            {b.nodeHostname ?? b.nodeId}
          </Link>
        ) : (
          'すべて'
        ),
    },
    { id: 'hits', header: '試行回数', align: 'right', hideBelow: 'lg', cell: (b) => formatCount(b.hitCount) },
    { id: 'createdAt', header: '遮断開始', hideBelow: 'md', cell: (b) => formatDateTime(b.createdAt) },
    {
      id: 'expiresAt',
      header: '解除予定',
      cell: (b) =>
        b.permanent ? (
          <Badge variant="destructive" size="sm">
            恒久
          </Badge>
        ) : (
          formatRelative(b.expiresAt)
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-28',
      align: 'right',
      cell: (b) => (canWrite ? <UnblockButton entry={b} /> : null),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="24h の新規遮断" value={formatCount(stats.data?.new24h)} icon={Ban} />
        <StatCard label="現在遮断中" value={formatCount(stats.data?.active)} icon={ShieldOff} tone="warning" />
        <StatCard label="24h の解除" value={formatCount(stats.data?.released24h)} />
      </div>

      <FilterBar
        q={search.q}
        onQChange={(q) => void navigate({ search: (prev) => ({ ...prev, q }) })}
        placeholder="IP / CIDR で絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: { tab: 'blocks' } })}
        selects={[
          {
            key: 'source',
            label: '理由',
            value: search.source,
            options: optionsFrom(IP_RULE_SOURCE, labelOf),
            onChange: (v) => void navigate({ search: (prev) => ({ ...prev, source: v as typeof search.source }) }),
          },
        ]}
      >
        <div className="ml-auto flex items-center gap-2">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
          {canWrite ? (
            <Button size="sm" onClick={() => setBlockOpen(true)}>
              <Ban aria-hidden />
              IP を手動遮断
            </Button>
          ) : null}
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(b) => b.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="遮断中の IP"
            onClearFilter={() => void navigate({ search: { tab: 'blocks' } })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <ManualBlockDialog open={blockOpen} onOpenChange={setBlockOpen} />
    </div>
  );
}

function UnblockButton({ entry }: { entry: BlocklistEntry }): React.ReactElement {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.del(`/blocklist/${entry.id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.blocklist() }),
  });
  return (
    <Button variant="ghost" size="xs" onClick={() => mut.mutate()} loading={mut.isPending}>
      解除
    </Button>
  );
}

function ManualBlockDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [cidr, setCidr] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [ttl, setTtl] = React.useState('86400');

  const mut = useMutation({
    mutationFn: () =>
      api.post('/blocklist', {
        cidr: cidr.trim(),
        reason,
        // ttlSec の下限・上限（60〜604800）はノード側 netguard の契約に合わせる。
        ttlSec: ttl === 'permanent' ? null : Number(ttl),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.blocklist() });
      void qc.invalidateQueries({ queryKey: qk.blocklistStats() });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="IP を手動で遮断"
      description="全ノードへ配布されます。誤って自社の送信元を入れると、その拠点からのメール送受信が止まります。"
      submitLabel="遮断する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={cidr.trim().length === 0}
      onSubmit={() => mut.mutate()}
    >
      <Field label="IP / CIDR" required hint="単一 IP なら /32 を省略できます。">
        <Input value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="203.0.113.45" className="font-mono" />
      </Field>
      <Field label="遮断期間" required>
        <NativeSelect
          value={ttl}
          onChange={setTtl}
          options={[
            { value: '3600', label: '1 時間' },
            { value: '86400', label: '1 日' },
            { value: '604800', label: '7 日（上限）' },
            { value: 'permanent', label: '恒久（手動で解除するまで）' },
          ]}
        />
      </Field>
      <Field label="理由" hint="監査ログに残ります。">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </FormDialog>
  );
}

/* -------------------------------------------------------------- 送信レート */

function SendingTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.orgWrite);

  const list = useCursorList<SendingViolation>({
    path: '/abuse/sending',
    keyFor: (params) => qk.sendingViolations(params),
    filters: {},
    refetchInterval: 30_000,
  });

  const columns: Array<DataTableColumn<SendingViolation>> = [
    { id: 'address', header: 'メールアドレス', cell: (v) => <span className="font-medium">{v.address}</span> },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (v) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: v.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {v.organizationName}
        </Link>
      ),
    },
    {
      id: 'sent',
      header: '24h 送信 / 上限',
      align: 'right',
      cell: (v) => {
        const ratio = ratioOf(v.sent24h, v.limit) ?? 0;
        return (
          <span className={cn('tabular', ratio >= 1 ? 'text-destructive' : ratio >= 0.8 ? 'text-warning' : undefined)}>
            {formatCount(v.sent24h)} / {formatCount(v.limit)}
          </span>
        );
      },
    },
    { id: 'sent1h', header: '直近 1h', align: 'right', hideBelow: 'md', cell: (v) => formatCount(v.sent1h) },
    {
      id: 'surge',
      header: '急増率',
      align: 'right',
      cell: (v) => (
        <span className={cn('tabular', v.surgeRatio >= 5 ? 'text-destructive' : v.surgeRatio >= 2 ? 'text-warning' : undefined)}>
          ×{v.surgeRatio.toFixed(1)}
        </span>
      ),
    },
    { id: 'status', header: '状態', cell: (v) => <StatusBadge status={v.status} size="sm" /> },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-32',
      align: 'right',
      cell: (v) => (canWrite ? <SuspendSendingButton violation={v} /> : null),
    },
  ];

  return (
    <>
      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(v) => v.mailboxId}
        loading={list.query.isLoading}
        emptyTitle="送信レートの異常はありません"
        emptyDescription="上限に近づいているメールボックスが出るとここに並びます。"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </>
  );
}

function SuspendSendingButton({ violation }: { violation: SendingViolation }): React.ReactElement {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.patch(`/mailboxes/${violation.mailboxId}`, { allowSubmission: false }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sendingViolations() });
      void qc.invalidateQueries({ queryKey: qk.mailboxes() });
    },
  });
  return (
    <Button variant="ghost" size="xs" className="text-destructive" onClick={() => mut.mutate()} loading={mut.isPending}>
      送信を止める
    </Button>
  );
}

/* ----------------------------------------------------------------- 迷惑行為 */

function AbuseTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);
  const [actionTarget, setActionTarget] = React.useState<{
    report: AbuseReport;
    action: AbuseAction;
  } | null>(null);

  const list = useCursorList<AbuseReport>({
    path: '/abuse/reports',
    keyFor: (params) => qk.abuseReports(params),
    filters: {},
  });

  /*
   * 理由の要らない措置（investigate / dismiss）だけを 1 クリックで送る。
   * **throttle / suspend は理由が必須**（サーバーが 400 で弾く）なので、
   * ダイアログで理由と停止範囲を集めてから送る。以前はどちらも理由なしで
   * 送っており、`throttle` は押しても必ず失敗していた。
   */
  const quick = useMutation({
    mutationFn: ({ id, act }: { id: string; act: 'dismiss' | 'investigate' }) =>
      api.post(`/abuse/reports/${id}/action`, { action: act }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.abuseReports() }),
  });

  const columns: Array<DataTableColumn<AbuseReport>> = [
    { id: 'detectedAt', header: '検知', width: 'w-44', cell: (r) => formatDateTime(r.detectedAt) },
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: r.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {r.organizationName}
        </Link>
      ),
    },
    { id: 'kind', header: '種別', cell: (r) => r.kind },
    {
      id: 'severity',
      header: '重大度',
      cell: (r) => (
        <Badge variant={r.severity === 'high' ? 'destructive' : r.severity === 'medium' ? 'warning' : 'muted'} size="sm">
          {r.severity === 'high' ? '高' : r.severity === 'medium' ? '中' : '低'}
        </Badge>
      ),
    },
    { id: 'summary', header: '概要', cell: (r) => <span className="text-sm text-muted-foreground">{r.summary}</span> },
    { id: 'state', header: '対応', cell: (r) => <StatusBadge status={r.state === 'open' ? 'pending' : r.state === 'actioned' ? 'succeeded' : 'skipped'} size="sm" /> },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (r) =>
        canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="この通報への措置">
                <MoreHorizontal aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => setActionTarget({ report: r, action: 'throttle' })}>
                <Gauge className="size-4" aria-hidden />
                送信上限を下げる
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setActionTarget({ report: r, action: 'release' })}>
                <Undo2 className="size-4" aria-hidden />
                送信上限を戻す
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => quick.mutate({ id: r.id, act: 'investigate' })}
                disabled={quick.isPending}
              >
                <Search className="size-4" aria-hidden />
                調査中にする
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => quick.mutate({ id: r.id, act: 'dismiss' })}
                disabled={quick.isPending}
              >
                却下する
              </DropdownMenuItem>
              {r.organizationId ? (
                <DropdownMenuItem
                  className="text-destructive"
                  onSelect={() => setActionTarget({ report: r, action: 'suspend' })}
                >
                  <ShieldOff className="size-4" aria-hidden />
                  利用を停止する
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null,
    },
  ];

  return (
    <>
      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(r) => r.id}
        loading={list.query.isLoading}
        emptyTitle="未対応の迷惑行為レポートはありません"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <AbuseActionDialog target={actionTarget} onClose={() => setActionTarget(null)} />
    </>
  );
}

type AbuseAction = 'throttle' | 'suspend' | 'release';

const ABUSE_ACTION_TITLE: Record<AbuseAction, string> = {
  throttle: '送信上限を下げる',
  suspend: '利用を停止する',
  release: '送信上限を戻す',
};

/**
 * 実害を伴う措置（`POST /abuse/reports/:id/action`）。
 *
 * サーバーは `.strict()` で、**`throttle` / `suspend` には 5 文字以上の理由が必須**。
 * `suspend` の停止範囲（`mode`）は運営者が選ぶ（既定は送信停止）。
 * 措置はラベル書き換えではなく実際にノードへ反映される（desired-state の再発行を伴う）。
 */
function AbuseActionDialog({
  target,
  onClose,
}: {
  target: { report: AbuseReport; action: AbuseAction } | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [mode, setMode] = React.useState<SuspendMode>('outbound');
  const [dailyLimit, setDailyLimit] = React.useState('50');

  React.useEffect(() => {
    if (target) {
      setReason('');
      setMode('outbound');
      setDailyLimit('50');
    }
  }, [target]);

  const mut = useMutation({
    mutationFn: () => {
      if (!target) return Promise.resolve(undefined);
      const body: Record<string, unknown> = { action: target.action, reason: reason.trim() };
      if (target.action === 'suspend') body.mode = mode;
      if (target.action === 'throttle') body.dailyLimit = Number(dailyLimit);
      return api.post(`/abuse/reports/${target.report.id}/action`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.abuseReports() });
      void qc.invalidateQueries({ queryKey: qk.orgs() });
      onClose();
    },
  });

  if (!target) return null;

  const limitValue = Number(dailyLimit);
  const limitInvalid = !Number.isInteger(limitValue) || limitValue < 0;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${ABUSE_ACTION_TITLE[target.action]}（${target.report.organizationName}）`}
      description="この措置は実際にノードへ反映されます。理由は監査ログと契約者への通知に残ります。"
      submitLabel="実行する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={reason.trim().length < 5 || (target.action === 'throttle' && limitInvalid)}
      onSubmit={() => mut.mutate()}
    >
      {target.action === 'suspend' ? (
        <Field label="停止の範囲" hint="既定は送信のみの停止（受信は止めません）。">
          <NativeSelect
            value={mode}
            onChange={(v) => setMode(v as SuspendMode)}
            options={SUSPEND_MODE_OPTIONS}
          />
        </Field>
      ) : null}

      {target.action === 'throttle' ? (
        <Field
          label="引き下げ後の日次送信上限"
          required
          hint="元の値は控えられ、「送信上限を戻す」で復旧します。"
          error={limitInvalid ? '0 以上の整数で入力してください。' : undefined}
        >
          <Input
            type="number"
            min={0}
            step={1}
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            inputMode="numeric"
          />
        </Field>
      ) : null}

      <Field label="理由" required hint="5 文字以上。何を根拠に措置したかを書いてください。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------ 稼働中の代理セッション */

/**
 * **今どの運営者がどの契約者に入っているか。**
 *
 * 代理ログインは通信の秘密に触れる操作なので、運営内部で相互に見えていることが
 * 統制の前提になる。ここが無いと、席を外した運営者のセッションを誰も止められず、
 * 最大 60 分のあいだ顧客データが開いたままになる。
 * 参照は `ops.security.read`（auditor も持つ）、終了は `ops.impersonate`。
 */
function ImpersonationsTab(): React.ReactElement {
  const qc = useQueryClient();
  const canEnd = usePermission(OPS_PERMISSION.impersonate);
  const [showAll, setShowAll] = React.useState(false);

  const query = useQuery({
    queryKey: qk.impersonations({ all: showAll }),
    queryFn: () => fetchImpersonations({ all: showAll }),
    refetchInterval: 30_000,
  });

  const end = useMutation({
    mutationFn: (id: string) => endImpersonationById(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.impersonations() }),
  });

  const columns: Array<DataTableColumn<ImpersonationSession>> = [
    { id: 'staff', header: '運営者', cell: (s) => <span className="font-medium">{s.staffLabel}</span> },
    {
      id: 'org',
      header: '契約組織',
      cell: (s) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: s.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {s.organizationName ?? s.organizationId}
        </Link>
      ),
    },
    {
      id: 'scope',
      header: '権限',
      cell: (s) => (
        <Badge variant={s.scope === 'read_write' ? 'destructive' : 'muted'} size="sm">
          {s.scope === 'read_write' ? '参照と変更' : '参照のみ'}
        </Badge>
      ),
    },
    { id: 'reason', header: '理由', cell: (s) => <span className="text-sm text-muted-foreground">{s.reason}</span> },
    { id: 'startedAt', header: '開始', hideBelow: 'md', cell: (s) => formatDateTime(s.startedAt) },
    {
      id: 'expiresAt',
      header: '期限',
      hideBelow: 'md',
      cell: (s) =>
        s.endedAt ? (
          <span className="text-muted-foreground">終了済（{formatRelative(s.endedAt)}）</span>
        ) : (
          formatDateTime(s.expiresAt)
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-28',
      align: 'right',
      cell: (s) =>
        s.active && canEnd ? (
          <Button
            variant="ghost"
            size="xs"
            className="text-destructive"
            onClick={() => end.mutate(s.id)}
            loading={end.isPending}
          >
            終了させる
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          代理セッションは最大 60 分で自動的に切れますが、用が済んだら明示的に終了させてください。
        </p>
        <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)}>
          {showAll ? '稼働中のみ表示' : '終了済みも表示'}
        </Button>
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={query.data ?? []}
        rowKey={(s) => s.id}
        loading={query.isLoading}
        emptyTitle={showAll ? '代理セッションの記録はありません' : '稼働中の代理セッションはありません'}
        emptyDescription="運営者が契約者として操作している間だけ、ここに行が出ます。"
      />
    </>
  );
}
